// Upsell policy: given the user's current plan and a `gate` describing what
// they tried to do, decide whether the upsell modal should appear and what
// it should offer.
//
// Routing:
//   - Gates any paid plan satisfies (ads, support) → Starter only.
//   - Gates where Starter satisfies but Solo's full toolkit is a
//     meaningfully different next step (pro-icon) → Starter and Solo.
//   - Gates that require Team (multi-seat) or where the user already has
//     teammates assigned → Team only.
//   - Everything else → Solo and Team, user picks.
//
// targetSkus are always annual SKUs (FA-SOLO, FA-TEAM, FA-STARTER). For
// gates that semantically require annual (downloads, npm, Figma, perpetual
// license), monthly variants wouldn't satisfy the gate at all — so pinning
// annual via targetSkus is the right contract.

import { get } from 'lodash'

import { UNLIMITED, resourceMeta } from 'app/views/accounts/addOns/addOnResources'
import { ANNUAL_SKU_FOR_MONTHLY } from 'app/views/plans/PlansData'

export const GATES = {
  PRO_ICON: 'pro-icon',
  PRO_PLUS_ICON: 'pro-plus-icon',
  PRO_PLUS_DOWNLOAD: 'pro-plus-download',
  ICON_WIZARD: 'icon-wizard',
  ICON_DOWNLOAD: 'icon-download',
  KIT_DOWNLOAD: 'kit-download',
  NPM: 'npm',
  FIGMA: 'figma',
  PERPETUAL_LICENSE: 'perpetual-license',
  ADD_SEAT: 'add-seat',
  CUSTOM_ICON_LIMIT: 'custom-icon-limit',
  KITS_LIMIT: 'kits-limit',
  ADS_FREE: 'ads-free',
  PRO_SUPPORT: 'pro-support'
}

// Pre-launch (2026-07-14) upsell events used verbose snake_case `feature`
// values; new events use the kebab-case GATES above. This maps old → new so a
// dashboard can group them — it is not applied at runtime.
export const LEGACY_FEATURE_ALIASES = {
  downloading_this_icon: GATES.ICON_DOWNLOAD,
  downloading_font_awesome_pro: GATES.ICON_DOWNLOAD,
  using_this_pro_icon: GATES.PRO_ICON,
  using_this_pro_plus_icon: GATES.PRO_PLUS_ICON,
  removing_advertisements: GATES.ADS_FREE
}

// "and" lists what exists; "or" asks the customer to pick one.
export function formatPlanList(names, conjunction = 'and') {
  if (names.length <= 1) return names[0] || ''

  return `${names.slice(0, -1).join(', ')} ${conjunction} ${names[names.length - 1]}`
}

// The one sentence template every blocked-entitlement surface uses, so the
// cycle-vs-Cloud wording can only ever be edited in one place. The cloud
// branch names annual tiers without offering a cycle switch — a Cloud
// customer sent to a monthly plan would land back on the same gate.
export function blockedFeatureSentence({ noun, blockReason, planName, upgradeList, includedList }) {
  if (blockReason === 'billing-cycle') {
    return `${noun} aren't included on monthly plans. Switch to annual billing to get them.`
  }

  if (blockReason === 'cloud') {
    const plan = planName ? `${planName} plans` : 'your plan'
    const upgrade = upgradeList ? ` Upgrade to an annual ${upgradeList} plan.` : ''

    return `${noun} aren't included on ${plan}.${upgrade}`
  }

  return includedList ? `${noun} are included on annual ${includedList} plans.` : `${noun} are included on our annual plans.`
}

// The offered tiers as "Solo and Team", or null until every one has a name —
// a half-loaded plans store would otherwise drop a tier from the sentence.
function offeredTierList({ targetSkus = [], targetPlanNames = [] }) {
  if (!targetSkus.length || targetPlanNames.length !== targetSkus.length) return null

  return formatPlanList(targetPlanNames)
}

// "Team includes 20 Kits." — built from the plans actually on offer, so the
// line never names one the customer was filtered away from. When the add-on is
// the only offer, it points at the current plan instead.
function limitSupport({ targetPlans = [], outcome, field, noun }) {
  if (outcome === 'add-on') return `You can add more ${noun} to your current plan.`
  // No plan raises the limit and no add-on is open to them, so the modal's own
  // contact message ("We'd be happy to help…") does the talking. Falling through
  // claimed "Higher plans include more Kits." to a 20-Kit customer no plan beats.
  if (outcome === 'contact') return ''

  const amounts = targetPlans.filter((plan) => typeof plan[field] === 'number')
  if (!amounts.length) return `Higher plans include more ${noun}.`

  const phrases = amounts.map((plan) => `${plan.name} includes ${plan[field] === UNLIMITED ? 'unlimited' : plan[field].toLocaleString('en-US')} ${noun}`)
  return `${phrases.join('; ')}.`
}

// Header copy per gate. The modal renders `headline` as the bold title and
// `support` as the supporting sentence beneath it; `icon` + `iconStyle`
// drive the inline glyph.
//
// `support` may be a string or a function
// `({ getPlan, blockReason, currentPlan, targetPlanNames }) => string`, so copy
// can interpolate live plan data rather than drift against hardcoded numbers.
// `currentPlan` is entitlement-aware and null when there is no paid plan.
//
// Headline tone:
//   - Diagnostic ("This is a Pro icon") for gates where the user just
//     bumped into something gated they didn't know about (icon clicks,
//     limit hits framed as a question).
//   - Prescriptive ("Get …", "Download …") for gates where the user
//     intentionally invoked an action that requires a plan change.
const proPlusSupport = ({ targetSkus, targetPlanNames, outcome }) => {
  if (outcome === 'contact') return 'Included on our larger plans.'
  if (outcome === 'add-on' || outcome === 'billing-owner') return 'Pro+ can be added to your current plan.'

  const tiers = offeredTierList({ targetSkus, targetPlanNames })
  return tiers ? `Included on ${tiers} plans.` : 'Included on Solo and Team plans.'
}

export const GATE_COPY = {
  [GATES.PRO_ICON]: {
    headline: 'This is a Pro icon',
    support: 'Included on every paid plan.',
    icon: 'faIcons',
    iconStyle: 'solid'
  },
  [GATES.PRO_PLUS_ICON]: {
    headline: 'This is a Pro+ icon',
    support: proPlusSupport,
    icon: 'faShapes',
    iconStyle: 'solid'
  },
  [GATES.PRO_PLUS_DOWNLOAD]: {
    headline: 'Download Font Awesome Pro+',
    support: proPlusSupport,
    icon: 'faArrowDownToLine',
    iconStyle: 'solid'
  },
  [GATES.ICON_WIZARD]: {
    headline: 'Unlock the Icon Wizard',
    support: 'Combine and remix custom icons on any paid plan.',
    icon: 'faHandHoldingMagic',
    iconStyle: 'solid'
  },
  [GATES.ICON_DOWNLOAD]: {
    headline: 'Download Pro Icons',
    support: ({ blockReason, currentPlan, targetSkus, targetPlanNames, outcome }) => {
      if (blockReason !== 'billing-cycle' && blockReason !== 'cloud') {
        if (outcome === 'contact') return 'Included on our annual plans.'

        const tiers = offeredTierList({ targetSkus, targetPlanNames })
        return tiers ? `Included on annual ${tiers} plans.` : 'Included on annual Solo and Team plans.'
      }

      return blockedFeatureSentence({
        noun: 'Downloads',
        blockReason,
        planName: currentPlan?.name,
        upgradeList: formatPlanList(targetPlanNames || [], 'or')
      })
    },
    icon: 'faArrowDownToLine',
    iconStyle: 'solid'
  },
  // The next four gates are unmounted — nothing passes them to
  // `owa-modal-upsell` today, so none of this renders. Kept rather than
  // deleted: KIT_DOWNLOAD and NPM lost their callers when the Kit Setup
  // Wizard was removed and both blocks are explained inline instead, so
  // they are the ones worth restoring if a modal comes back. FIGMA and
  // PERPETUAL_LICENSE were never wired to anything.
  [GATES.KIT_DOWNLOAD]: {
    headline: 'Download a Kit',
    support: 'Included on annual Solo and Team plans.',
    icon: 'faArrowDownToLine',
    iconStyle: 'solid'
  },
  [GATES.NPM]: {
    headline: 'Get the Font Awesome npm package',
    support: 'Included on annual Solo and Team plans.',
    icon: 'faBox',
    iconStyle: 'solid'
  },
  [GATES.FIGMA]: {
    headline: 'Get the Figma plugin',
    support: 'Included on annual Solo and Team plans.',
    icon: 'faPenNib',
    iconStyle: 'solid'
  },
  [GATES.PERPETUAL_LICENSE]: {
    headline: 'Get a perpetual license',
    support: 'Annual Solo and Team include a perpetual license — keep icons released during your subscription, forever.',
    icon: 'faFileCertificate',
    iconStyle: 'solid'
  },
  [GATES.ADD_SEAT]: {
    headline: 'Add teammates with Team',
    support: 'Team starts at 3 seats and scales to 250.',
    icon: 'faUserGroup',
    iconStyle: 'solid'
  },
  [GATES.CUSTOM_ICON_LIMIT]: {
    headline: 'Need more Custom Icons?',
    support: ({ targetPlans, outcome }) => limitSupport({ targetPlans, outcome, field: 'customIcons', noun: 'Custom Icons' }),
    icon: 'faCloudArrowUp',
    iconStyle: 'solid'
  },
  [GATES.KITS_LIMIT]: {
    headline: 'Need more Kits?',
    support: ({ targetPlans, outcome }) => limitSupport({ targetPlans, outcome, field: 'kits', noun: 'Kits' }),
    icon: 'faSuitcase',
    iconStyle: 'solid'
  },
  [GATES.ADS_FREE]: {
    headline: 'Remove ads',
    support: 'Every paid plan removes ads across the site.',
    icon: 'faBillboard',
    iconStyle: 'solid'
  },
  [GATES.PRO_SUPPORT]: {
    headline: 'Get actual human support',
    support: 'Every paid plan includes priority support from real humans.',
    icon: 'faHeadset',
    iconStyle: 'solid'
  }
}

// Gates that an add-on can satisfy without a plan change, mapped to the
// `add_on_products` resource key (025-subscription-addons). The two hard-cap
// resources have limit gates; pageviews/bandwidth are metered and get their
// add-on prompt from the usage banners instead. Pro+ is the one capability an
// add-on sells. Every other capability gate (Pro icons, npm, Figma, seats)
// stays upgrade-only.
export const GATE_ADD_ON_RESOURCES = {
  [GATES.KITS_LIMIT]: 'kits',
  [GATES.CUSTOM_ICON_LIMIT]: 'icon_uploads',
  [GATES.PRO_PLUS_ICON]: 'pro_plus',
  [GATES.PRO_PLUS_DOWNLOAD]: 'pro_plus'
}

export function addOnResourceForGate(gate) {
  return GATE_ADD_ON_RESOURCES[gate] || null
}

// Gates that semantically require multi-seat support.
const TEAM_REQUIRED_GATES = new Set([GATES.ADD_SEAT])

// Gates that any paid plan satisfies — push to Starter, the lowest-friction
// entry point, instead of the Solo/Team default.
const STARTER_SUFFICIENT_GATES = new Set([GATES.ADS_FREE, GATES.PRO_SUPPORT])

// Gates where Starter satisfies the gate AND the next tier offers a
// qualitatively different value prop worth surfacing alongside it
// (e.g., Pro icon access: Starter is CDN-only, Solo adds the full toolkit).
const STARTER_PLUS_SOLO_GATES = new Set([GATES.PRO_ICON, GATES.ICON_WIZARD])

// The gates decided by `proDownloadEnabled` — the only ones that can be
// cycle-blocked. npm is absent on purpose: it reads `privateRegistriesEnabled`.
export const DOWNLOAD_ENTITLEMENT_GATES = new Set([GATES.ICON_DOWNLOAD, GATES.KIT_DOWNLOAD, GATES.FIGMA])

// Returns true when the user's current plan already covers the feature this
// gate represents — in which case the modal shouldn't have been triggered.
//
// `seatChangesAllowed` comes off the subscription, not the plan: it's the
// server's answer from `Plans.seat_changes_allowed?/1`, which whitelists six
// SKUs. The plan-level `seatUpgradeEnabled` column is NOT a substitute — Lite
// carries `seatUpgradeEnabled: true` in the legacy schema but is a single-seat
// plan in our taxonomy, so keying off it would suppress the upsell for users
// who genuinely can't add seats.
//
// `servesProPlus` is the subscription's own answer to "has Pro+ now", which
// counts the Pro+ add-on as well as the plan, so an add-on holder is never
// offered Pro+ again.
export function currentPlanSatisfiesGate(currentPlan, gate, { seatChangesAllowed = false, effectiveLimits = null, servesProPlus = false } = {}) {
  if (!currentPlan) return false

  switch (gate) {
    case GATES.KITS_LIMIT:
      return get(effectiveLimits, 'kits') === UNLIMITED
    case GATES.CUSTOM_ICON_LIMIT:
      return get(effectiveLimits, 'customIcons') === UNLIMITED
    case GATES.PRO_ICON:
      return !!currentPlan.proEnabled
    case GATES.PRO_PLUS_ICON:
      return servesProPlus === true || get(currentPlan, ['iconCollection', 'name']) === 'pro-plus-collection'
    case GATES.PRO_PLUS_DOWNLOAD:
      // The server's rule: Pro+ (plan or add-on) on a plan with downloads.
      return !!currentPlan.proDownloadEnabled && (servesProPlus === true || get(currentPlan, ['iconCollection', 'name']) === 'pro-plus-collection')
    case GATES.ICON_WIZARD:
      // Any paid plan unlocks the Icon Wizard; `proEnabled` is the plan-level
      // proxy (false on Free, true on every paid tier).
      return !!currentPlan.proEnabled
    case GATES.ICON_DOWNLOAD:
    case GATES.KIT_DOWNLOAD:
    case GATES.FIGMA:
      // All three gated by proDownloadEnabled in the comparison table.
      return !!currentPlan.proDownloadEnabled
    case GATES.NPM:
      return !!currentPlan.privateRegistriesEnabled
    case GATES.PERPETUAL_LICENSE:
      return !!currentPlan.perpetualLicenseEnabled
    case GATES.ADD_SEAT:
      return seatChangesAllowed === true
    case GATES.ADS_FREE:
      // Any paid plan removes ads; `proEnabled` is the closest plan-level
      // proxy (false on Free, true everywhere else).
      return !!currentPlan.proEnabled
    case GATES.PRO_SUPPORT:
      return !!currentPlan.supportEnabled
    default:
      return false
  }
}

// Why downloads are off for this plan:
//
//   'billing-cycle' — right tier, wrong cycle. Switching to annual fixes it.
//   'cloud'         — the plan never included downloads on either cycle;
//                     only a tier change fixes it.
//   'unknown'       — a monthly plan whose annual counterpart isn't loaded
//                     yet, so which of the two above applies can't be said.
//
// A monthly plan is classified by its annual counterpart, not by being
// monthly: Starter Monthly's counterpart has downloads off too, so switching
// cycle wouldn't unblock it and it belongs in the cloud bucket.
//
// 'unknown' exists so a cold plans store can't produce a confident wrong
// answer. Guessing 'cloud' there would tell a Solo Monthly customer
// "Downloads aren't included on Solo plans" — false, since annual Solo has
// them. Every caller treats 'unknown' as "say nothing plan-specific" and
// falls back to the neutral line, and `requiresCounterpartLookup` marks
// exactly this population so `ensurePlansLoaded` can resolve it.
//
// `currentPlan` must be entitlement-aware — null without an active paid
// subscription. See `ModalUpsell.entitledPlan`.
// Whether a plan carries download rights. Not the `proCdn` permit: that
// governs a different, sunsetted product and denies Pro Lite, which would
// read here as "Pro Lite has downloads".
export function planBlocksDownloads(plan) {
  return !!plan && !plan.proDownloadEnabled
}

export function downloadBlockReason({ currentPlan, getPlan }) {
  if (!currentPlan) return 'no-paid-plan'
  if (!planBlocksDownloads(currentPlan)) return null
  if (currentPlan.annual !== false) return 'cloud'

  const counterpartSku = ANNUAL_SKU_FOR_MONTHLY[currentPlan.sku]
  // A monthly plan with no annual twin has no cycle to switch to.
  if (!counterpartSku) return 'cloud'

  const counterpart = getPlan && getPlan(counterpartSku)
  if (!counterpart) return 'unknown'

  return counterpart.proDownloadEnabled ? 'billing-cycle' : 'cloud'
}

// True when classifying this plan needs the annual counterpart out of the
// plans store — lets callers fetch plans only when the answer would
// otherwise be wrong.
export function requiresCounterpartLookup(currentPlan) {
  return !!currentPlan && currentPlan.annual === false && !currentPlan.proDownloadEnabled
}

// The plan field a limit gate is about (`kits` / `customIcons`), or null.
function gatedPlanField(gate) {
  const resource = addOnResourceForGate(gate)
  return resource ? resourceMeta(resource).planField : null
}

const CAPACITY_FIELDS = ['kits', 'customIcons']

// Whether `plan` fits the customer: at least what they use now of every
// capacity resource, and on a limit gate strictly more of the gated one than
// they can already use. The plan's included amount stands alone because a plan
// change drops held add-ons. A plan record that hasn't loaded yet is kept —
// the modal isn't revealed until plans load, and this runs again when they do.
export function planCovers({ plan, gate, usage = {}, effectiveLimits = null }) {
  if (!plan) return true

  const gatedField = gatedPlanField(gate)

  return CAPACITY_FIELDS.every((field) => {
    const amount = plan[field]
    if (typeof amount !== 'number' || amount === UNLIMITED) return true

    const inUse = usage[field] || 0
    if (field !== gatedField) return amount >= inUse

    return amount > Math.max(inUse, get(effectiveLimits, field, 0) || 0)
  })
}

// What the modal shows once the targets are settled. `plans` may still carry
// the add-on row beneath the cards; the modal decides that from `addOn`.
function resolveOutcome({ gate, targetSkus, addOn }) {
  if (targetSkus.length) return 'plans'
  if (!addOnResourceForGate(gate)) return 'contact'
  if (addOn.purchasable) return 'add-on'
  if (addOn.blockedReason === 'billing-owner') return 'billing-owner'

  return 'contact'
}

// Main policy entry. `currentPlan` is the user's active plan record (null
// for logged-out / free users); `gate` is one of GATES; `currentSeatUsage`
// is the number of seats the user has actually assigned to teammates
// (defaults to 0 when unknown); `seatChangesAllowed` is the subscription's
// server-computed seat-management eligibility (defaults to false when unknown,
// so an unresolved subscription shows the upsell rather than suppressing it);
// `getPlan` tells a cycle block from a Cloud block and supplies each target's
// included Kits and Custom Icons; without it the download gates fall back to
// the tier-acquisition targets. `usage` and `effectiveLimits` are the
// customer's current counts and plan-plus-add-on limits; `servesProPlus` is
// the subscription's "has Pro+ now", add-on included; `addOn` says whether
// the gate's resource can be bought on the current plan.
//
// Returns:
//   {
//     action: 'show-plan' | 'suppress',
//     reason: string,                       // when suppressed
//     targetSkus: ['FA-SOLO' | 'FA-TEAM' | ...],
//     outcome: 'plans' | 'add-on' | 'billing-owner' | 'contact' | null
//   }
export function upsellPolicy({
  currentPlan,
  gate,
  currentSeatUsage = 0,
  seatChangesAllowed = false,
  getPlan = null,
  usage = {},
  effectiveLimits = null,
  servesProPlus = false,
  addOn = NO_ADD_ON
}) {
  if (currentPlanSatisfiesGate(currentPlan, gate, { seatChangesAllowed, effectiveLimits, servesProPlus })) {
    return suppressed()
  }

  const result = resolveTargets({ currentPlan, gate, currentSeatUsage, getPlan })

  // Never offer someone the plan they already pay for. On a capability gate,
  // emptying the list that way means their plan covered it after all. A limit
  // gate can't be covered by a plan, so it falls through to an add-on or a
  // message instead.
  const targetSkus = result.targetSkus.filter((sku) => sku !== currentPlan?.sku)

  if (!targetSkus.length && !addOnResourceForGate(gate)) {
    return suppressed()
  }

  const coveredSkus = targetSkus.filter((sku) => planCovers({ plan: getPlan && getPlan(sku), gate, usage, effectiveLimits }))

  return { ...result, targetSkus: coveredSkus, outcome: resolveOutcome({ gate, targetSkus: coveredSkus, addOn }) }
}

function suppressed() {
  return { action: 'suppress', reason: 'feature-already-included', targetSkus: [], outcome: null }
}

const NO_ADD_ON = Object.freeze({ resource: null, purchasable: false, blockedReason: null })

function resolveTargets({ currentPlan, gate, currentSeatUsage, getPlan }) {
  // A cycle block has exactly one remedy: the same tier on annual billing.
  if (DOWNLOAD_ENTITLEMENT_GATES.has(gate) && getPlan) {
    if (downloadBlockReason({ currentPlan, getPlan }) === 'billing-cycle') {
      return {
        action: 'show-plan',
        reason: 'billing-cycle',
        targetSkus: [ANNUAL_SKU_FOR_MONTHLY[currentPlan.sku]]
      }
    }
  }

  // Some gates are satisfied by any paid plan — Starter is the
  // lowest-friction recommendation in that case (e.g., ad removal,
  // human support).
  if (STARTER_SUFFICIENT_GATES.has(gate)) {
    return {
      action: 'show-plan',
      targetSkus: ['FA-STARTER']
    }
  }

  // Starter satisfies the gate, but Solo's full toolkit is a meaningfully
  // different next step. Show both so the user picks between CDN-only
  // (Starter) and the full toolkit (Solo).
  if (STARTER_PLUS_SOLO_GATES.has(gate)) {
    return {
      action: 'show-plan',
      targetSkus: ['FA-STARTER', 'FA-SOLO']
    }
  }

  // Default to surfacing both Solo and Team — most gates are satisfied by
  // either tier (the support copy explicitly says so) and the user picks
  // based on whether they want teammates. Filter Solo out only when the
  // gate semantically requires Team or the user is already running
  // multi-seat — Solo's 1 seat would be a real downgrade in that case.
  const isTeamGate = TEAM_REQUIRED_GATES.has(gate)
  const hasMultiSeat = currentSeatUsage > 1
  const teamOnly = isTeamGate || hasMultiSeat

  return {
    action: 'show-plan',
    targetSkus: teamOnly ? ['FA-TEAM'] : ['FA-SOLO', 'FA-TEAM']
  }
}
